import { forwardRef, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { addDays, addYears } from 'date-fns';
import {
  CustodianTypes,
  DefaultUserManagersResponse,
  FreelancerAccountTypes,
  IndependentIraUserRequestForm,
  ProfileListResponse,
  UserManagerInfo,
  UserProfileTypes,
} from 'services/requests';
import { IraAccountFieldNames } from 'hooks/forms/useIraAccountsForm';
import {
  convertDate,
  etzFeeTransform,
  annualFeeTransform,
  lowerCaseTransform,
  custodyFeePercentageTransform,
  custodyFeeAbsoluteTransform,
  formatInputDate,
  parseInputDate,
  checkDefaultUserManager,
  cellNumberTransform,
} from 'utils';
import { USER_STATUS } from 'constants/status';
import { mapIndependentIraTypeValueToKey } from 'screens/IndependentIra/IraIndependentIra.utils';
import Picker from 'components/Picker';
import Paper from 'components/Paper';
import Field from 'components/Field';
import { AlertLockUser, AlertsList } from 'components/Alerts';
import ProfileList from 'components/ProfileList';
import DateField from 'components/DateField';
import UserManager from 'components/UserManager';
import {
  disabledCustodian,
  disabledEmail,
  disabledField,
  disabledNextBillingDate,
  enabledName,
} from './IndependentIraForm.statuses';

import styles from './IndependentIraForm.module.css';
import messages from './IndependentIraForm.messages';

interface Props {
  form: UseFormReturn<IndependentIraUserRequestForm>;
  profileStatus?: string;
  profileType?: string;
  lastActivity?: string;
  accountNumber?: string;
  userName?: string;
  phoneNumber?: string;
  created?: string;
  userDataIsInconsistent?: boolean;
  isNew: boolean;
  hasOtherAccount?: boolean;
  isLocked?: boolean;
  hoursToAutoUnlock?: number;
  lockReason?: string;
  profileList?: ProfileListResponse;
  email?: string;
  errors?: string[];
  approvalDateTime?: string;
  nextBillingDate?: string;
  defaultEtzFee?: number;
  defaultCustodyFeePercentageIraClub?: number;
  custodianType?: string;
  shouldEditCustodian?: boolean;
  userManagers?: UserManagerInfo[];
  defaultUserManager?: DefaultUserManagersResponse;
}

const IndependentIraForm = forwardRef<HTMLFormElement, Props>(
  (
    {
      form,
      profileStatus,
      profileType = '',
      lastActivity,
      accountNumber,
      userName,
      phoneNumber,
      created,
      isNew,
      userDataIsInconsistent,
      hasOtherAccount,
      isLocked,
      hoursToAutoUnlock,
      lockReason,
      profileList,
      email,
      errors,
      approvalDateTime,
      nextBillingDate,
      defaultEtzFee,
      defaultCustodyFeePercentageIraClub,
      custodianType,
      shouldEditCustodian,
      userManagers,
      defaultUserManager,
    },
    ref,
  ) => {
    const fieldLength = {
      firstName: 150,
      middleName: 150,
      lastName: 150,
    };
    const RenderSubtitle = (): JSX.Element =>
      isNew ? (
        <div className={styles.iraAccountsForm__subtitle}>
          <span>{messages.subtitle}</span>
        </div>
      ) : (
        <div className={styles.iraAccountsForm__subtitle}>
          <p>
            {!!created && <span>{`${messages.created}${convertDate(created)}`}</span>}
            {!!accountNumber && (
              <span>{`${messages.accountNumber} #${accountNumber}`}</span>
            )}
          </p>
          <p>
            {!!lastActivity ? (
              <span>{`${messages.activity} ${convertDate(lastActivity)}`}</span>
            ) : (
              <span>{messages.noActivity}</span>
            )}
            {!!userName && <span>{`${messages.userName} ${userName}`}</span>}
          </p>
          <p>
            {custodianType === CustodianTypes.solera ? (
              <span>
                {!!approvalDateTime
                  ? `${messages.approvalDateTime} ${convertDate(approvalDateTime)}`
                  : messages.noApprovalDateTime}
              </span>
            ) : (
              <span>
                {!!nextBillingDate
                  ? `${messages.nextBillingDate} ${convertDate(
                      parseInputDate(nextBillingDate).toISOString(),
                    )}`
                  : messages.noNextBillingDate}
              </span>
            )}
            {!!phoneNumber && (
              <span>{`${messages.phoneNumber} ${cellNumberTransform(phoneNumber)}`}</span>
            )}
          </p>
        </div>
      );

    const [profileTypeSelected, setProfileTypeSelected] = useState<
      string | number | undefined
    >(profileType);
    const [custodianSelected, setCustodianSelected] = useState<
      string | number | undefined
    >(custodianType);
    const [annualFeeFocused, setAnnualFeeFocused] = useState(false);
    const [custodyFeePercentageFocused, setCustodyFeePercentageFocused] = useState(false);
    const [custodyFeeAbsoluteFocused, setCustodyFeeAbsoluteFocused] = useState(false);

    useEffect(() => {
      setCustodianSelected(custodianType);
    }, [custodianType]);

    const isDisabled =
      (!!profileStatus && disabledField(profileStatus)) || !!userDataIsInconsistent;
    const isDisabledNames =
      (!!profileStatus && !enabledName(profileStatus)) || isDisabled || hasOtherAccount;
    const isDisabledEmail =
      (!!profileStatus && disabledEmail(profileStatus)) || isDisabled || isNew;
    const isDisabledNextBillingDate =
      (!!profileStatus && disabledNextBillingDate(profileStatus)) || isNew;
    const isDisabledCustodian =
      ((!!profileStatus && disabledCustodian(profileStatus)) ||
        !!userDataIsInconsistent) &&
      !shouldEditCustodian;
    const isDisabledAnnualFee = custodianSelected !== CustodianTypes.solera || isDisabled;
    const definedDefaultUserManager =
      (isNew || custodianSelected !== custodianType) &&
      defaultUserManager &&
      checkDefaultUserManager(defaultUserManager, custodianSelected);
    const shouldShowUserManagerAlert =
      !!userManagers?.length &&
      !userManagers[0]?.custodianType &&
      custodianSelected !== custodianType;
    const linkedUserManagers =
      (shouldShowUserManagerAlert && userManagers) ||
      (custodianSelected === custodianType && userManagers);
    const profileTypeWatch = form.watch(IraAccountFieldNames.profileType);
    const disabledBusinessName =
      profileTypeWatch === '' ||
      !FreelancerAccountTypes.includes(profileTypeWatch as any);

    const { dirtyFields } = form.formState;

    const onChangeCustodyFee = () => {
      if (profileStatus === USER_STATUS.active && approvalDateTime) {
        const values = form.getValues();
        const nextBillingDate = values[IraAccountFieldNames.nextBillingDate];
        const custodyFeeAbsolute = values[IraAccountFieldNames.custodyFeeAbsolute];
        const custodyFeePercentage = values[IraAccountFieldNames.custodyFeePercentage];

        if ((custodyFeeAbsolute || custodyFeePercentage) && !nextBillingDate) {
          const currentDatePlusDay = addDays(new Date(), 1);
          const approvalDatePlusYear = addYears(new Date(approvalDateTime), 1);
          const newNextBillingDate = formatInputDate(
            approvalDatePlusYear > currentDatePlusDay
              ? approvalDatePlusYear
              : currentDatePlusDay,
          ).replace(/[^\d]/g, '') as never;

          form.setValue(IraAccountFieldNames.nextBillingDate, newNextBillingDate, {
            shouldDirty: true,
          });
        }
      }
    };

    const onChangeCustodianType = (newCustodian: string | number | undefined) => {
      setCustodianSelected(newCustodian);

      form.setValue(IraAccountFieldNames.etzFee, defaultEtzFee as never, {
        shouldDirty: true,
      });
      form.setValue(
        IraAccountFieldNames.custodyFeePercentage,
        defaultCustodyFeePercentageIraClub?.toString() as never,
        {
          shouldDirty: true,
        },
      );
    };

    const watchCustodianTypeField = form.watch(IraAccountFieldNames.custodianType);
    const watchProfileTypeField = form.watch(IraAccountFieldNames.profileType);

    useEffect(() => {
      if (watchCustodianTypeField === '') {
        setCustodianSelected(undefined);
      }
      if (
        Object.values(CustodianTypes).find(
          (custodianType) => custodianType === watchCustodianTypeField,
        )
      ) {
        setCustodianSelected(watchCustodianTypeField);
      }
    }),
      [watchCustodianTypeField];

    useEffect(() => {
      if (watchProfileTypeField === '') {
        setProfileTypeSelected(undefined);
      }
      if (
        Object.values(UserProfileTypes).find(
          (independentIraType) => independentIraType === watchProfileTypeField,
        )
      ) {
        setProfileTypeSelected(watchProfileTypeField);
      }
    }),
      [watchProfileTypeField];

    useEffect(() => {
      if (disabledBusinessName) {
        form.setValue(IraAccountFieldNames.businessName, '' as never);
        form.clearErrors(IraAccountFieldNames.businessName);
      }
    }, [form, disabledBusinessName]);

    return (
      <>
        <DevTool control={form.control} />
        <Paper className={styles.iraAccountsForm}>
          <AlertLockUser
            hoursToAutoUnlock={hoursToAutoUnlock}
            lockReason={lockReason}
            isLocked={isLocked}
          />
          {!!errors?.length && <AlertsList errors={errors} />}

          <h1 className={styles.iraAccountsForm__title}>{messages.title}</h1>
          <RenderSubtitle />

          <form ref={ref}>
            <Field
              type={'text'}
              form={form}
              name={IraAccountFieldNames.firstName}
              placeholder={messages.placeholder.firstName}
              className={styles.iraAccountsForm__input}
              labelClassname={styles.iraAccountsForm__inputLabel}
              disabled={isDisabledNames}
              maxLength={fieldLength.firstName}
            />

            <Field
              type={'text'}
              form={form}
              name={IraAccountFieldNames.middleName}
              placeholder={messages.placeholder.middleName}
              className={styles.iraAccountsForm__input}
              labelClassname={styles.authUserForm__inputLabel}
              disabled={isDisabledNames}
              maxLength={fieldLength.middleName}
            />

            <Field
              type={'text'}
              form={form}
              name={IraAccountFieldNames.lastName}
              placeholder={messages.placeholder.lastName}
              className={styles.iraAccountsForm__input}
              labelClassname={styles.iraAccountsForm__inputLabel}
              disabled={isDisabledNames}
              maxLength={fieldLength.lastName}
            />

            <Field
              type={'text'}
              form={form}
              name={IraAccountFieldNames.email}
              placeholder={messages.placeholder.email}
              className={styles.iraAccountsForm__input}
              labelClassname={styles.iraAccountsForm__inputLabel}
              disabled={isDisabledEmail}
              {...lowerCaseTransform}
            />

            {!isDisabled ? (
              <Picker
                form={form}
                name={IraAccountFieldNames.profileType}
                options={[
                  UserProfileTypes.traditional,
                  UserProfileTypes.roth,
                  UserProfileTypes.inheritedTraditional,
                  UserProfileTypes.inheritedRoth,
                  UserProfileTypes.hsa,
                  UserProfileTypes.esa,
                  UserProfileTypes.sepFreelancer,
                  UserProfileTypes.simpleIra,
                  UserProfileTypes.solo401kFreelancer,
                  UserProfileTypes.rothSolo401k,
                  UserProfileTypes.rothSepIra,
                  UserProfileTypes.rothSimpleIra,
                  UserProfileTypes.corporate401kSubAccount,
                ]}
                placeholder={messages.placeholder.type}
                className={styles.iraAccountsForm__input}
                onSelect={setProfileTypeSelected}
                selectedOption={profileTypeSelected}
                size={'large'}
              />
            ) : (
              <Field
                type={'text'}
                form={form}
                name={IraAccountFieldNames.profileType}
                placeholder={messages.placeholder.type}
                className={styles.iraAccountsForm__input}
                labelClassname={styles.iraAccountsForm__inputLabel}
                disabled={true}
              />
            )}

            <Picker
              form={form}
              name={IraAccountFieldNames.custodianType}
              options={Object.values(CustodianTypes)}
              placeholder={messages.placeholder.custodianType}
              className={styles.iraAccountsForm__input}
              onSelect={onChangeCustodianType}
              selectedOption={custodianSelected}
              size={'large'}
              disabled={isDisabledCustodian}
              disabledOption={CustodianTypes.solera}
            />

            <Field
              type={'text'}
              form={form}
              name={IraAccountFieldNames.businessName}
              placeholder={`${messages.placeholder.businessName}${
                disabledBusinessName ? '' : '*'
              }`}
              className={styles.iraAccountsForm__input}
              labelClassname={styles.iraAccountsForm__inputLabel}
              disabled={disabledBusinessName}
            />

            <Field
              type={'text'}
              form={form}
              name={IraAccountFieldNames.etzFee}
              placeholder={messages.placeholder.etzFee}
              className={styles.iraAccountsForm__input}
              labelClassname={styles.iraAccountsForm__inputLabel}
              {...etzFeeTransform}
            />

            <Field
              type={'text'}
              form={form}
              name={IraAccountFieldNames.annualFee}
              placeholder={messages.placeholder.annualFee}
              className={styles.iraAccountsForm__input}
              labelClassname={styles.iraAccountsForm__inputLabel}
              disabled={isDisabledAnnualFee}
              onFocus={() => setAnnualFeeFocused(true)}
              onBlur={() => setAnnualFeeFocused(false)}
              mask={(value) =>
                annualFeeTransform.mask(value, { ignoreEmpty: annualFeeFocused })
              }
              mapper={annualFeeTransform.mapper}
            />

            <p className={styles.iraAccountsForm__fieldsGroupTitle}>
              {messages.custodyFeeTitle}
            </p>

            <DateField
              form={form}
              name={IraAccountFieldNames.nextBillingDate}
              placeholder={
                !isDisabledNextBillingDate &&
                (dirtyFields.custodyFeePercentage || dirtyFields.custodyFeeAbsolute)
                  ? `${messages.placeholder.nextBillingDate}*`
                  : messages.placeholder.nextBillingDate
              }
              className={styles.iraAccountsForm__input}
              labelClassname={styles.iraAccountsForm__inputLabel}
              from={addDays(new Date(), 1)}
              disabled={isDisabledNextBillingDate}
            />

            <Field
              type={'text'}
              form={form}
              name={IraAccountFieldNames.custodyFeePercentage}
              placeholder={
                dirtyFields.nextBillingDate && !dirtyFields.custodyFeeAbsolute
                  ? `${messages.placeholder.custodyFeePercentage}*`
                  : messages.placeholder.custodyFeePercentage
              }
              className={styles.iraAccountsForm__input}
              labelClassname={styles.iraAccountsForm__inputLabel}
              onFocus={() => setCustodyFeePercentageFocused(true)}
              onBlur={() => setCustodyFeePercentageFocused(false)}
              mask={(value) =>
                custodyFeePercentageTransform.mask(value, {
                  isFocused: custodyFeePercentageFocused,
                })
              }
              mapper={custodyFeePercentageTransform.mapper}
              onChangeText={onChangeCustodyFee}
            />

            <Field
              type={'text'}
              form={form}
              name={IraAccountFieldNames.custodyFeeAbsolute}
              placeholder={
                dirtyFields.nextBillingDate && !dirtyFields.custodyFeePercentage
                  ? `${messages.placeholder.custodyFeeAbsolute}*`
                  : messages.placeholder.custodyFeeAbsolute
              }
              className={styles.iraAccountsForm__input}
              labelClassname={styles.iraAccountsForm__inputLabel}
              onFocus={() => setCustodyFeeAbsoluteFocused(true)}
              onBlur={() => setCustodyFeeAbsoluteFocused(false)}
              mask={(value) =>
                custodyFeeAbsoluteTransform.mask(value, {
                  ignoreEmpty: custodyFeeAbsoluteFocused,
                })
              }
              mapper={custodyFeeAbsoluteTransform.mapper}
              onChangeText={onChangeCustodyFee}
            />
          </form>

          {(linkedUserManagers ||
            (definedDefaultUserManager && !shouldShowUserManagerAlert)) && (
            <UserManager
              userManagers={linkedUserManagers || undefined}
              defaultUserManager={
                shouldShowUserManagerAlert
                  ? undefined
                  : definedDefaultUserManager || undefined
              }
              showAlert={shouldShowUserManagerAlert}
            />
          )}

          <ProfileList
            otherAccounts={profileList}
            email={email}
            currentType={mapIndependentIraTypeValueToKey(profileType)}
          />
        </Paper>
      </>
    );
  },
);

export default IndependentIraForm;
